import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Gavel, HeartPulse, ShieldCheck, ShoppingCart } from "lucide-react";

import { useI18n } from "@/lib/i18n";
import { articleSchema, breadcrumbSchema, faqPageSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { HeroImage } from "@/components/hero-image";
import { safetyVialImage } from "@/lib/responsive-images";

const safetyFaqs: Record<"de" | "en", { q: string; a: string }[]> = {
  de: [
    { q: "Wie komme ich an Retatrutid?", a: "Retatrutid ist kein frei verkäufliches Fertigarzneimittel. Ein sicherer Zugang ist ausschließlich über genehmigte klinische Studienprogramme möglich – etwa über unsere Partner-Klinik in Bangkok, die geeignete Teilnehmer im Rahmen eines genehmigten Studienprogramms behandelt." },
    { q: "Ist Retatrutid in Deutschland oder der EU zugelassen?", a: "Nein. Retatrutid ist als Fertigarzneimittel weder in der EU noch in den USA zugelassen. Die reguläre Zulassung durch FDA und EMA wird nach Abschluss des TRIUMPH-Phase-III-Programms für 2026/27 erwartet." },
    { q: "Sind Retatrutid-Angebote von Peptid-Websites sicher?", a: "Nein. Angebote als „Research Peptide“ oder „nur zu Forschungszwecken“ sind nicht Teil eines genehmigten Studienprogramms. Reinheit, Dosierung und Sterilität sind nicht arzneimittelrechtlich geprüft; die Anwendung birgt erhebliche Gesundheitsrisiken." },
    { q: "Welche ärztliche Begleitung ist bei Retatrutid nötig?", a: "Wie bei allen Wirkstoffen dieser Klasse ist qualifizierte ärztliche Betreuung Pflicht. Überwacht werden mindestens Blutdruck, Puls, Blutzucker, Pankreas-Enzyme und Nierenfunktion." },
    { q: "Welche zugelassenen Alternativen gibt es zu Retatrutid?", a: "Zugelassene Alternativen mit vergleichbarem Mechanismus sind Semaglutid (Wegovy, Ozempic) und Tirzepatid (Mounjaro, Zepbound) – ausschließlich per Rezept und unter ärztlicher Kontrolle." },
  ],
  en: [
    { q: "How do I get Retatrutide?", a: "Retatrutide is not an over-the-counter finished pharmaceutical. Safe access is only possible through approved clinical study programs, including our partner clinic in Bangkok that treats eligible participants inside an approved study program." },
    { q: "Is Retatrutide approved in the EU or US?", a: "No. Retatrutide is still in the regulatory approval phase as a finished pharmaceutical in both the EU and US. Regular FDA and EMA approval is expected around 2026/27 after completion of the TRIUMPH Phase III program." },
    { q: "Are Retatrutide offers from peptide websites safe?", a: "No. Offers labelled 'research peptide' or 'for research only' are not part of an approved study program. Purity, dosage and sterility are not vetted under pharmaceutical law and use carries substantial health risks." },
    { q: "What medical supervision does Retatrutide require?", a: "As with all compounds in this class, qualified medical supervision is mandatory. At minimum, blood pressure, pulse, blood glucose, pancreatic enzymes and kidney function must be monitored." },
    { q: "What approved alternatives to Retatrutide exist?", a: "Approved alternatives with a comparable mechanism are Semaglutide (Wegovy, Ozempic) and Tirzepatide (Mounjaro, Zepbound) — prescription-only and under medical supervision." },
  ],
};

export const Route = createFileRoute("/sicherheit")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const lang = loaderData?.lang ?? "en";
    return {
      ...localizedHead("/sicherheit", loaderData?.lang),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/sicherheit", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/sicherheit", loaderData?.lang, { specialty: "Pharmacologic", aspect: "Contraindication" })) },
        { type: "application/ld+json", children: JSON.stringify(faqPageSchema("/sicherheit", loaderData?.lang, safetyFaqs[lang])) },
        { type: "application/ld+json", children: JSON.stringify(articleSchema("/sicherheit", loaderData?.lang, { section: "Safety" })) },
      ],
    };
  },
  component: Page,
});




function Page() {
  const { t } = useI18n();
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-20">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-warning">
          {t.safety.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {t.safety.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{t.safety.lead}</p>
      </div>

      <HeroImage
        {...safetyVialImage}
        alt="Editorial macro photograph of a pharmaceutical vial under dramatic side light"
        eager
      />


      <section className="mt-10 space-y-4">
        <div className="card-elevated flex gap-4 p-6">
          <Gavel className="h-6 w-6 flex-shrink-0 text-indigo" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.safety.legal.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t.safety.legal.body1}
              <strong className="text-foreground">{t.safety.legal.strong}</strong>
              {t.safety.legal.body2}
            </p>
          </div>
        </div>

        <div className="card-elevated flex gap-4 border-warning/40 bg-warning/5 p-6">
          <AlertTriangle className="h-6 w-6 flex-shrink-0 text-warning" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.safety.warn.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t.safety.warn.body1}
              <strong className="text-foreground">{t.safety.warn.strong}</strong>
              {t.safety.warn.body2}
            </p>
          </div>
        </div>

        <div className="card-elevated flex gap-4 p-6">
          <HeartPulse className="h-6 w-6 flex-shrink-0 text-emerald" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.safety.med.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.safety.med.body}</p>
          </div>
        </div>

        <div className="card-elevated flex gap-4 p-6">
          <ShieldCheck className="h-6 w-6 flex-shrink-0 text-indigo" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.safety.todo.title}</h2>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {t.safety.todo.items.map((it) => (
                <li key={it}>{it}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <BuyingSection />

      <ConsultCta variant="section" />
      <RelatedLinks only={["/vergleich", "/zulassung", "/nebenwirkungen"]} />
    </div>
  );
}

function BuyingSection() {
  const { lang } = useI18n();
  const de = lang === "de";
  const c = de
    ? {
        eyebrow: "Retatrutid erhalten?",
        h1: "Wie komme ich an Retatrutid?",
        lead: "Kurz: Retatrutid ist kein frei verkäufliches Fertigarzneimittel. Ein sicherer Zugang ist ausschließlich über genehmigte klinische Studienprogramme möglich – wie das unserer Partner-Klinik. Was Sie wissen sollten:",
        points: [
          { t: "Klinischer Studienzugang", b: "Retatrutid ist als Fertigarzneimittel weder in der EU noch in den USA zugelassen. Es kann aber im Rahmen genehmigter klinischer Studien – einschließlich unserer Partner-Klinik – für geeignete Teilnehmer bereitgestellt werden." },
          { t: "Nicht alle Quellen sind gleich", b: "Angebote auf Peptid-Websites deklarieren Retatrutid oft als „nur zu Forschungszwecken“. Diese Produkte sind nicht Teil eines genehmigten Studienprogramms – Reinheit, Dosierung und Sterilität sind nicht arzneimittelrechtlich geprüft." },
          { t: "Ärztliche Begleitung", b: "Wie bei allen Wirkstoffen dieser Klasse ist eine qualifizierte ärztliche Betreuung Pflicht: Blutdruck, Puls, Blutzucker, Pankreas-Enzyme und Nierenfunktion müssen überwacht werden." },
          { t: "Zugelassene Alternativen", b: "Zugelassene Alternativen mit vergleichbarem Mechanismus: Semaglutid (Wegovy/Ozempic) und Tirzepatid (Mounjaro/Zepbound) – ausschließlich per Rezept und unter ärztlicher Kontrolle." },
        ],
        cta1: "Vergleich der Alternativen",
        cta2: "Zulassungs-Timeline",
      }
    : {
        eyebrow: "Getting Retatrutide?",
        h1: "How do I get Retatrutide?",
        lead: "Short answer: Retatrutide is not an over-the-counter finished pharmaceutical. Safe access is only possible through approved clinical study programs, such as our partner clinic's. What you should know:",
        points: [
          { t: "Clinical study access", b: "Retatrutide is still in the regulatory approval phase as a finished pharmaceutical in the EU and US. It can, however, be provided within approved clinical studies — including our partner clinic's — for eligible participants." },
          { t: "Not every source is equal", b: "Offers on peptide sites often label Retatrutide as 'for research only'. These products are not part of an approved study program — purity, dosage and sterility are not vetted under pharmaceutical law." },
          { t: "Medical supervision", b: "As with all compounds in this class, qualified medical supervision is mandatory: blood pressure, pulse, blood glucose, pancreatic enzymes and kidney function must be monitored." },
          { t: "Approved alternatives", b: "Approved alternatives with a comparable mechanism: Semaglutide (Wegovy/Ozempic) and Tirzepatide (Mounjaro/Zepbound) — prescription-only, under medical supervision." },
        ],
        cta1: "Compare the alternatives",
        cta2: "Approval timeline",
      };

  return (
    <section className="mt-16">
      <div className="rounded-2xl border border-warning/40 bg-warning/5 p-6 md:p-10">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-warning/15 text-warning">
            <ShoppingCart className="h-5 w-5" />
          </span>
          <p className="text-xs font-semibold uppercase tracking-widest text-warning">
            {c.eyebrow}
          </p>
        </div>
        <h2 className="mt-4 font-display text-3xl leading-tight text-foreground md:text-4xl">
          {c.h1}
        </h2>
        <p className="mt-3 text-muted-foreground">{c.lead}</p>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {c.points.map((p) => (
            <div key={p.t} className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-base font-semibold text-foreground">{p.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.b}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Link
            to="/vergleich"
            className="inline-flex items-center rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground hover:opacity-90"
          >
            {c.cta1}
          </Link>
          <Link
            to="/zulassung"
            className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
          >
            {c.cta2}
          </Link>
        </div>
      </div>
    </section>
  );
}

